import { useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Tag,
  Input,
  Alert,
  Table,
  Popconfirm,
  Divider,
  message,
  Spin,
  Steps,
} from 'antd';
import {
  SafetyOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  LaptopOutlined,
} from '@ant-design/icons';
import { useSetup2FA, useConfirm2FA, useSessions, useRevokeSession } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import type { Admin2FASetupResponse, AdminSessionInfo } from '../../types/auth';

const { Title, Text, Paragraph } = Typography;

export function SecuritySettingsPage() {
  const adminUser = useAuthStore((s) => s.adminUser);
  const setUser = useAuthStore((s) => s.setUser);
  const is2FAEnabled = adminUser?.admin_2fa_enabled ?? false;

  const [setupStep, setSetupStep] = useState(0);
  const [setupData, setSetupData] = useState<Admin2FASetupResponse | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [showSetup, setShowSetup] = useState(false);

  const setup2FA = useSetup2FA();
  const confirm2FA = useConfirm2FA();
  const sessions = useSessions();
  const revokeSession = useRevokeSession();

  const handleStartSetup = async () => {
    try {
      const data = await setup2FA.mutateAsync();
      setSetupData(data);
      setSetupStep(0);
      setShowSetup(true);
    } catch {
      message.error('Failed to initialize 2FA setup');
    }
  };

  const handleConfirm2FA = async () => {
    if (verifyCode.length !== 6) {
      message.warning('Enter a 6-digit code');
      return;
    }
    try {
      await confirm2FA.mutateAsync({ code: verifyCode });
      setShowSetup(false);
      setSetupData(null);
      setVerifyCode('');
      if (adminUser) {
        setUser({ ...adminUser, admin_2fa_enabled: true });
      }
    } catch {
      message.error('Invalid code. Please try again.');
    }
  };

  const handleCopyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      message.success('Backup codes copied to clipboard');
    }
  };

  const sessionColumns = [
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip: string | null) => ip || 'Unknown',
    },
    {
      title: 'Browser / Device',
      dataIndex: 'user_agent',
      key: 'user_agent',
      render: (ua: string | null) => {
        if (!ua) return 'Unknown';
        if (ua.length > 60) return ua.substring(0, 60) + '...';
        return ua;
      },
    },
    {
      title: 'Last Active',
      dataIndex: 'last_activity_at',
      key: 'last_activity_at',
      render: (dt: string | null) => dt ? new Date(dt).toLocaleString() : 'N/A',
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (dt: string) => new Date(dt).toLocaleString(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: AdminSessionInfo) => (
        <Popconfirm
          title="Revoke this session?"
          description="The session will be terminated immediately."
          onConfirm={() => revokeSession.mutate(record.id)}
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            Revoke
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 800 }}>
      <Title level={2}>
        <SafetyOutlined /> Security Settings
      </Title>

      {/* 2FA Status Card */}
      <Card
        title="Two-Factor Authentication (2FA)"
        extra={
          is2FAEnabled ? (
            <Tag icon={<CheckCircleOutlined />} color="success">Enabled</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="warning">Not Enabled</Tag>
          )
        }
        style={{ marginBottom: 24 }}
      >
        {is2FAEnabled ? (
          <Space direction="vertical">
            <Text>
              Two-factor authentication is active on your account. You will be prompted for a
              verification code from your authenticator app each time you log in.
            </Text>
            <Text type="secondary">
              If you lose access to your authenticator app, use one of your backup codes to log in.
            </Text>
          </Space>
        ) : (
          <Space direction="vertical" size="middle">
            <Alert
              type="warning"
              showIcon
              message="2FA is not enabled"
              description={
                adminUser?.admin_2fa_setup_deadline
                  ? `You must enable 2FA before ${new Date(adminUser.admin_2fa_setup_deadline).toLocaleDateString()}. After this date, you will be locked out.`
                  : 'Enable 2FA to secure your admin account.'
              }
            />
            {!showSetup && (
              <Button
                type="primary"
                icon={<SafetyOutlined />}
                onClick={handleStartSetup}
                loading={setup2FA.isPending}
              >
                Enable Two-Factor Authentication
              </Button>
            )}
          </Space>
        )}

        {/* 2FA Setup Wizard */}
        {showSetup && setupData && (
          <>
            <Divider />
            <Steps
              current={setupStep}
              size="small"
              items={[
                { title: 'Scan QR Code' },
                { title: 'Save Backup Codes' },
                { title: 'Verify' },
              ]}
              style={{ marginBottom: 24 }}
            />

            {setupStep === 0 && (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Title level={5}>Step 1: Scan this QR code with your authenticator app</Title>
                <Paragraph type="secondary">
                  Use Google Authenticator, Authy, or any TOTP-compatible app.
                </Paragraph>
                {setupData.qr_code ? (
                  <div style={{ textAlign: 'center', padding: 16, background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <img src={setupData.qr_code} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
                  </div>
                ) : (
                  <Alert type="info" message="QR code generation unavailable. Use the manual key below." />
                )}
                <div>
                  <Text type="secondary">Or enter this key manually:</Text>
                  <br />
                  <Text code copyable style={{ fontSize: 14 }}>{setupData.secret}</Text>
                </div>
                <Button type="primary" onClick={() => setSetupStep(1)}>
                  Next: Save Backup Codes
                </Button>
              </Space>
            )}

            {setupStep === 1 && (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Title level={5}>Step 2: Save your backup codes</Title>
                <Alert
                  type="warning"
                  showIcon
                  message="Save these codes in a safe place"
                  description="Each backup code can only be used once. If you lose your authenticator app, these are the only way to access your account."
                />
                <div style={{ background: '#fafafa', padding: 16, borderRadius: 8, border: '1px solid #f0f0f0', fontFamily: 'monospace' }}>
                  {setupData.backup_codes.map((code, i) => (
                    <div key={i} style={{ padding: '4px 0' }}>{code}</div>
                  ))}
                </div>
                <Button icon={<CopyOutlined />} onClick={handleCopyBackupCodes}>
                  Copy All Codes
                </Button>
                <Space>
                  <Button onClick={() => setSetupStep(0)}>Back</Button>
                  <Button type="primary" onClick={() => setSetupStep(2)}>
                    Next: Verify Code
                  </Button>
                </Space>
              </Space>
            )}

            {setupStep === 2 && (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Title level={5}>Step 3: Enter verification code</Title>
                <Paragraph type="secondary">
                  Enter the 6-digit code from your authenticator app to complete setup.
                </Paragraph>
                <Input
                  size="large"
                  placeholder="000000"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  onPressEnter={handleConfirm2FA}
                  style={{ width: 200, textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
                />
                <Space>
                  <Button onClick={() => setSetupStep(1)}>Back</Button>
                  <Button
                    type="primary"
                    onClick={handleConfirm2FA}
                    loading={confirm2FA.isPending}
                    disabled={verifyCode.length !== 6}
                  >
                    Verify & Enable 2FA
                  </Button>
                </Space>
              </Space>
            )}
          </>
        )}
      </Card>

      {/* Active Sessions Card */}
      <Card
        title={<><LaptopOutlined /> Active Sessions</>}
      >
        {sessions.isLoading ? (
          <Spin />
        ) : sessions.data?.sessions.length === 0 ? (
          <Text type="secondary">No active sessions found.</Text>
        ) : (
          <Table
            dataSource={sessions.data?.sessions ?? []}
            columns={sessionColumns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Card>
    </div>
  );
}
